import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { noAutoClose, toastConfig } from 'assets/data/config';
import StylishViewFileDialog from 'components/DesignSystems/New/StylishViewFileDialog';
import { exportedFormFilesKeys } from 'components/FileManagement/hooks/useExportedFormFiles';
import { filesOrFoldersKeys } from 'components/FileManagement/hooks/useGetAllFiles';
import {
  ICS_204_CG_FORM_NAME,
  postExportWorkAssignmentListAttachment,
  type Ics204DocxExportResult,
  type Ics204PdfExportWithPreview,
} from 'components/IAP/hooks/assignmentListHooks';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import {
  useCurrentPeriod,
  useIncident,
} from 'components/IAP/hooks/incidentHooks';
import {
  ExportPlannedResourceAsFormParams,
  ICS_204A_FORM_NAME,
  postExportPlannedResourceAsForm,
  type Ics204aExportResult,
} from 'components/IAP/hooks/workAssignmentListAttachmentHooks';
import {
  ExportAsFormParams,
  postExportAsForm,
  postPersistIcsFormExport,
} from 'hooks/incident/useExportForm';
import { postExportPaginatedIap } from 'hooks/incident/useExportPaginatedIAP';
import { formatIapFormName } from 'components/IAP/iapAttachmentManifest';
import {
  selectIncidents,
  selectGroupGuid,
  selectUser,
  useAppSelector,
} from 'slices/commonSelectors';
import Sentry from '../../../instrument';
import downloadFile from 'utils/downloadFile';
import { base64ToObjectUrl } from 'utils/pdfPreview';
import { useSearchParams } from 'react-router';

type PreviewDownloadMode = 'base64' | 'signed' | 'none';
type PreviewExportFormat = 'pdf' | 'docx' | 'xlsx';

const WORD_OOXML_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function previewExtension(format: PreviewExportFormat): string {
  if (format === 'docx') return 'docx';
  if (format === 'xlsx') return 'xlsx';
  return 'pdf';
}

function previewMime(format: PreviewExportFormat): string {
  if (format === 'docx') return WORD_OOXML_MIME;
  if (format === 'xlsx') return XLSX_MIME;
  return 'application/pdf';
}

function previewDownloadType(format: PreviewExportFormat): string {
  if (format === 'docx') return 'docx';
  if (format === 'xlsx') return 'xlsx';
  return 'PDF';
}

function previewLabel(format: PreviewExportFormat): string {
  if (format === 'docx') return 'Word document';
  if (format === 'xlsx') return 'Excel workbook';
  return 'PDF';
}

type IcsFormPdfPersistMeta = {
  workspaceFormname: string;
  periodNumber?: number | null;
  incidentId?: string;
};

type Base64PreviewOpenOptions = {
  allowWorkspaceDownload?: boolean;
  /** When false, Download skips workspace PDF persist (used for Word). */
  persistToWorkspace?: boolean;
  format?: PreviewExportFormat;
  mimeType?: string;
  persistMeta: IcsFormPdfPersistMeta;
};

type IcsFormPdfPreviewContextValue = {
  exportAsForm: (
    params: ExportAsFormParams,
    options?: {
      onSuccess?: () => void;
      allowWorkspaceDownload?: boolean;
      incidentIdOverride?: string;
    }
  ) => Promise<void>;
  exportPlannedResourceAsForm: (
    params: ExportPlannedResourceAsFormParams
  ) => Promise<void>;
  exportWorkAssignmentListAttachment: (
    walaId: string,
    format?: 'pdf' | 'docx'
  ) => Promise<void>;
  /** Preview a document built on its own route (e.g. ICS-215 Word). */
  previewExportedForm: (args: {
    base64: string;
    formname: string;
    format?: PreviewExportFormat;
    periodNumber?: number;
  }) => void;
  exportPaginatedIAP: ({
    mapSketch,
    periodNumber,
    isFuture,
    incidentIdOverride,
  }: {
    mapSketch?: File;
    periodNumber?: number;
    /**
     * Whether `periodNumber` names the period being planned, which has no row
     * in the DB yet. Callers that already know should say so; the URL's `mode`
     * is only a fallback, and disagreeing with it sends the backend a period
     * that does not exist.
     */
    isFuture?: boolean;
    incidentIdOverride?: string;
  }) => Promise<void>;
  /** Preview a PDF or Word file from an already-resolved URL. */
  previewFromSignedUrl: (args: {
    fileUrl: string;
    fileName: string | null;
    onDownload: () => void | Promise<void>;
    mimeType?: string;
  }) => void;
  isExporting: boolean;
};

const IcsFormPdfPreviewContext =
  createContext<IcsFormPdfPreviewContextValue | null>(null);

function toastExportError(error: unknown, label: string) {
  const err = error as {
    response?: {
      data?: {
        ERROR?: string;
        message?: string;
        error?: { code?: string; message?: string };
      };
    };
    message?: string;
  };
  if (err?.response?.data?.ERROR === 'UNAUTHORIZED') {
    toast.error('You are not authorized to perform this action.');
    return;
  }
  const serverMessage =
    err?.response?.data?.error?.message ?? err?.response?.data?.message;
  if (serverMessage) {
    toast.error(serverMessage);
    return;
  }
  const errorMessage =
    `${label}\n\n` +
    ((err?.message && typeof err.message === 'string' && err.message) || '') +
    '\n\nIf you continue to experience this error, contact support@disastertech.com';
  toast.error(errorMessage, noAutoClose);
}

export function IcsFormPdfPreviewProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const { data: incident } = useIncident();
  const incidents = useAppSelector(selectIncidents);
  const user = useAppSelector(selectUser);
  const groupGuid = useAppSelector(selectGroupGuid);
  const { periodNumber } = useCurrentPeriod();
  const incidentId = useActualIncidentId();
  const [show, setShow] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [previewMimeType, setPreviewMimeType] = useState('application/pdf');
  const [downloadMode, setDownloadMode] = useState<PreviewDownloadMode>('none');
  const [searchParams] = useSearchParams();
  const resolveTargetIncident = useCallback(
    (incidentIdOverride?: string) => {
      if (incidentIdOverride) {
        const overrideIncident = incidents.find(
          (item) => item.id === incidentIdOverride
        );
        if (!overrideIncident) {
          toast.error(
            'Unable to open preview yet. Incident data is still loading.',
            toastConfig
          );
          return null;
        }
        return overrideIncident;
      }

      if (!incident) {
        toast.error(
          'Unable to open preview. No incident is selected.',
          toastConfig
        );
        return null;
      }

      return incident;
    },
    [incident, incidents]
  );

  const revokeObjectUrlRef = useRef<(() => void) | null>(null);
  const downloadFromBase64Ref = useRef<{
    stem: string;
    base64: string;
    allowWorkspaceDownload: boolean;
    persistToWorkspace: boolean;
    format: PreviewExportFormat;
    persistMeta: IcsFormPdfPersistMeta;
  } | null>(null);
  const signedDownloadRef = useRef<(() => void | Promise<void>) | null>(null);

  const [exportInFlight, setExportInFlight] = useState(0);
  const beginExport = useCallback(() => {
    setExportInFlight((n) => n + 1);
  }, []);
  const endExport = useCallback(() => {
    setExportInFlight((n) => Math.max(0, n - 1));
  }, []);

  const clearBlobPreview = useCallback(() => {
    revokeObjectUrlRef.current?.();
    revokeObjectUrlRef.current = null;
    downloadFromBase64Ref.current = null;
    signedDownloadRef.current = null;
    setDownloadMode('none');
  }, []);

  const closeModal = useCallback(() => {
    clearBlobPreview();
    setShow(false);
    setFileUrl('');
    setFileName(null);
    setPreviewMimeType('application/pdf');
  }, [clearBlobPreview]);

  const openBase64Preview = useCallback(
    (
      base64: string,
      modalTitle: string,
      downloadStem: string,
      openOptions: Base64PreviewOpenOptions
    ) => {
      const format = openOptions.format ?? 'pdf';
      clearBlobPreview();
      try {
        const mimeType = openOptions.mimeType ?? previewMime(format);
        const { objectUrl, revoke } = base64ToObjectUrl(base64, mimeType);
        revokeObjectUrlRef.current = revoke;
        downloadFromBase64Ref.current = {
          stem: downloadStem,
          base64,
          allowWorkspaceDownload: openOptions.allowWorkspaceDownload !== false,
          persistToWorkspace: openOptions.persistToWorkspace !== false,
          format,
          persistMeta: openOptions.persistMeta,
        };
        setDownloadMode('base64');
        setPreviewMimeType(mimeType);
        setFileUrl(objectUrl);
        setFileName(modalTitle);
        setShow(true);
      } catch (e) {
        toast.error(`Error preparing ${previewLabel(format)} preview.`);
        Sentry.captureException(e);
      }
    },
    [clearBlobPreview]
  );

  const previewFromSignedUrl = useCallback(
    (args: {
      fileUrl: string;
      fileName: string | null;
      onDownload: () => void | Promise<void>;
      mimeType?: string;
    }) => {
      clearBlobPreview();
      signedDownloadRef.current = args.onDownload;
      setDownloadMode('signed');
      setPreviewMimeType(args.mimeType || 'application/pdf');
      setFileUrl(args.fileUrl);
      setFileName(args.fileName);
      setShow(true);
    },
    [clearBlobPreview]
  );

  const handleHeaderDownload = useCallback(async () => {
    if (downloadMode === 'base64' && downloadFromBase64Ref.current) {
      const {
        stem,
        base64,
        allowWorkspaceDownload,
        persistToWorkspace,
        format,
        persistMeta,
      } = downloadFromBase64Ref.current;
      if (!allowWorkspaceDownload) {
        return;
      }
      const label = previewLabel(format);
      try {
        const persistIncident =
          incidents.find((item) => item.id === persistMeta.incidentId) ??
          incident;

        downloadFile(stem, previewDownloadType(format), base64);
        // Word exports are archived alongside the PDFs. An IAP's record of what
        // was published should not depend on which format a planner chose.
        if (persistToWorkspace) {
          await postPersistIcsFormExport(
            {
              incident: persistIncident,
              user,
              groupGuid,
            },
            {
              pdfBase64: base64,
              workspaceFormname: persistMeta.workspaceFormname,
              periodNumber: persistMeta.periodNumber,
              format,
            }
          );

          if (persistIncident?.id) {
            await queryClient.refetchQueries({
              queryKey: exportedFormFilesKeys.byIncident(persistIncident.id),
            });
            await queryClient.refetchQueries({
              queryKey: filesOrFoldersKeys.allFiles(persistIncident.id),
            });
          }
          toast.success(`${label} downloaded and updated`);
        } else {
          toast.success(`${label} downloaded`);
        }
      } catch (e) {
        toast.error('Error downloading or saving the form.');
        Sentry.captureException(e);
      }
      return;
    }
    if (downloadMode === 'signed' && signedDownloadRef.current) {
      void signedDownloadRef.current();
    }
  }, [downloadMode, groupGuid, incident, incidents, queryClient, user]);

  const exportAsForm = useCallback(
    async (
      params: ExportAsFormParams,
      options?: {
        onSuccess?: () => void;
        allowWorkspaceDownload?: boolean;
        incidentIdOverride?: string;
      }
    ) => {
      beginExport();
      try {
        const targetIncident = resolveTargetIncident(
          options?.incidentIdOverride
        );
        if (!targetIncident) {
          return;
        }

        // By default, the "Future Period" tab does not use an operational period
        // number — it uses ics_incident_info timestamp values instead. Callers
        // that need to override this (e.g. ICS 209, which always references the
        // current period regardless of tab) can pass params.periodNumber
        // explicitly and it will take precedence.
        const mode = searchParams.get('mode');
        const effectivePeriodNumber = (() => {
          if (params.periodNumber != null) return params.periodNumber;
          if (mode === 'future') return undefined;
          return periodNumber;
        })();

        const result = await postExportAsForm(
          {
            incident: targetIncident,
            user,
            groupGuid,
          },
          {
            ...params,
            periodNumber: effectivePeriodNumber,
          }
        );

        if (result.format === 'xlsx') {
          downloadFile(result.formname, 'xlsx', result.data);
          await postPersistIcsFormExport(
            {
              incident: targetIncident,
              user,
              groupGuid,
            },
            {
              pdfBase64: result.data,
              workspaceFormname: result.formname,
              periodNumber: effectivePeriodNumber,
              format: 'xlsx',
            }
          );
          if (targetIncident.id) {
            await queryClient.refetchQueries({
              queryKey: exportedFormFilesKeys.byIncident(targetIncident.id),
            });
            await queryClient.refetchQueries({
              queryKey: filesOrFoldersKeys.allFiles(targetIncident.id),
            });
          }
          toast.success('Excel workbook downloaded and updated');
          options?.onSuccess?.();
          return;
        }

        const title = `${result.formname.replace(/_/g, '-')}.${previewExtension(
          result.format
        )}`;

        openBase64Preview(result.data, title, result.formname, {
          allowWorkspaceDownload: options?.allowWorkspaceDownload !== false,
          persistToWorkspace: true,
          format: result.format,
          mimeType: previewMime(result.format),
          persistMeta: {
            workspaceFormname: result.formname,
            periodNumber: effectivePeriodNumber,
            incidentId: targetIncident?.id,
          },
        });
        options?.onSuccess?.();
      } catch (error) {
        toastExportError(error, 'exportAsFormError');
      } finally {
        endExport();
      }
    },
    [
      beginExport,
      endExport,
      groupGuid,
      openBase64Preview,
      periodNumber,
      queryClient,
      resolveTargetIncident,
      searchParams,
      user,
    ]
  );

  const exportPlannedResourceAsForm = useCallback(
    async (params: ExportPlannedResourceAsFormParams) => {
      beginExport();
      try {
        const result = await postExportPlannedResourceAsForm(
          periodNumber,
          params
        );
        const isDocx = result.format === 'docx';
        const title = `${ICS_204A_FORM_NAME.replace(/_/g, '-')}.${
          isDocx ? 'docx' : 'pdf'
        }`;
        const payload = result.data as Ics204aExportResult | string;
        const base64 =
          typeof payload === 'string'
            ? payload
            : ((isDocx ? payload?.docxBase64 : payload?.pdfBase64) ?? null);
        if (base64) {
          openBase64Preview(base64, title, result.formName, {
            format: result.format,
            mimeType: isDocx ? WORD_OOXML_MIME : 'application/pdf',
            persistMeta: {
              workspaceFormname: result.formName,
              periodNumber,
            },
          });
        }
      } catch (error) {
        toastExportError(error, 'exportAsFormError');
      } finally {
        endExport();
      }
    },
    [beginExport, endExport, openBase64Preview, periodNumber]
  );

  const exportWorkAssignmentListAttachment = useCallback(
    async (walaId: string, format: 'pdf' | 'docx' = 'pdf') => {
      beginExport();
      try {
        const result = await postExportWorkAssignmentListAttachment(
          incidentId,
          periodNumber,
          walaId,
          { includePreviewPages: true, format }
        );
        const isDocx = format === 'docx';
        const title = `${ICS_204_CG_FORM_NAME.replace(/_/g, '-')}.${
          isDocx ? 'docx' : 'pdf'
        }`;
        const payload = result as
          | Ics204PdfExportWithPreview
          | Ics204DocxExportResult
          | string;
        const base64 =
          typeof payload === 'string'
            ? payload
            : isDocx
              ? ((payload as Ics204DocxExportResult).docxBase64 ?? null)
              : ((payload as Ics204PdfExportWithPreview).pdfBase64 ?? null);
        if (base64) {
          openBase64Preview(base64, title, ICS_204_CG_FORM_NAME, {
            format,
            mimeType: isDocx ? WORD_OOXML_MIME : 'application/pdf',
            persistMeta: {
              workspaceFormname: ICS_204_CG_FORM_NAME,
              periodNumber,
            },
          });
        }
      } catch (error) {
        toastExportError(error, 'Export failed');
      } finally {
        endExport();
      }
    },
    [beginExport, endExport, incidentId, openBase64Preview, periodNumber]
  );

  /**
   * Opens a document a caller already built on its own route.
   *
   * ICS-215 Word comes from the worksheet-version route rather than
   * `exportAsForm`, but it should land in the same preview with the same
   * workspace archiving as every other form.
   */
  const previewExportedForm = useCallback(
    (args: {
      base64: string;
      formname: string;
      format?: PreviewExportFormat;
      periodNumber?: number;
    }) => {
      const format = args.format ?? 'pdf';
      openBase64Preview(
        args.base64,
        `${args.formname.replace(/_/g, '-')}.${previewExtension(format)}`,
        args.formname,
        {
          format,
          mimeType: previewMime(format),
          persistMeta: {
            workspaceFormname: args.formname,
            periodNumber: args.periodNumber ?? periodNumber,
            incidentId,
          },
        }
      );
    },
    [incidentId, openBase64Preview, periodNumber]
  );

  const exportPaginatedIAP = useCallback(
    async ({
      mapSketch,
      periodNumber: callerPeriodNumber,
      isFuture: callerIsFuture,
      incidentIdOverride,
    }: {
      mapSketch?: File;
      periodNumber?: number;
      isFuture?: boolean;
      incidentIdOverride?: string;
    }) => {
      beginExport();
      try {
        const targetIncident = resolveTargetIncident(incidentIdOverride);
        if (!targetIncident) {
          return;
        }

        // The caller's own notion of "future" wins. A widget can be rendered in
        // future mode without `mode=future` being on the URL, and when the two
        // disagree the period below is one the backend has no row for.
        const isFuture =
          callerIsFuture ?? searchParams.get('mode') === 'future';

        // Tag (filename prefix IAP-OP{N}-) always uses the target period number
        // so the inline viewer's regex lookup matches.
        const tagPeriodNumber = (() => {
          if (callerPeriodNumber != null) return callerPeriodNumber;
          if (isFuture && periodNumber != null) return periodNumber + 1;
          return periodNumber;
        })();

        // In future mode the next period has not been created in the DB yet,
        // so omit periodNumber — the backend falls back to ics_incident_info
        // next-period timestamps.
        const backendPeriodNumber = isFuture ? undefined : tagPeriodNumber;

        const formnameStem = `${(targetIncident as { name?: string })?.name ?? 'Incident'} IAP`;
        const result = await postExportPaginatedIap(
          {
            incident: targetIncident,
            user,
            groupGuid,
            formnameStem,
            mapSketch,
          },
          backendPeriodNumber
        );
        const title = `${formnameStem}.pdf`;
        openBase64Preview(result.data, title, result.downloadStem, {
          persistMeta: {
            workspaceFormname: 'IAP',
            periodNumber: tagPeriodNumber,
            incidentId: targetIncident?.id,
          },
        });

        // Generating publishes the plan, so the workspace copy is written here
        // rather than waiting for a download. The cover cannot be approved
        // until an IAP exists, and closing the preview should not undo that.
        try {
          await postPersistIcsFormExport(
            { incident: targetIncident, user, groupGuid },
            {
              pdfBase64: result.data,
              workspaceFormname: 'IAP',
              periodNumber: tagPeriodNumber,
              format: 'pdf',
            }
          );
          if (targetIncident?.id) {
            await queryClient.refetchQueries({
              queryKey: exportedFormFilesKeys.byIncident(targetIncident.id),
            });
            await queryClient.refetchQueries({
              queryKey: filesOrFoldersKeys.allFiles(targetIncident.id),
            });
          }
        } catch (error) {
          // The document is already on screen; say the archiving failed rather
          // than losing the generated plan to it.
          toast.error(
            'The IAP was generated but could not be saved to the workspace.'
          );
          Sentry.captureException(error);
        }

        // The cover leaves these unchecked, but say so plainly rather than
        // letting the user discover the gap in the printed plan.
        if (result.omittedForms.length > 0) {
          toast.warning(
            `Left out of the IAP for lack of content: ${result.omittedForms
              .map(formatIapFormName)
              .join(', ')}.`
          );
        }
        if (result.omittedAttachments.length > 0) {
          toast.warning(
            `Could not append: ${result.omittedAttachments
              .map(({ label, reason }) => `${label} (${reason})`)
              .join(', ')}.`
          );
        }
      } catch (error) {
        toastExportError(error, 'exportPaginatedIAPError');
      } finally {
        endExport();
      }
    },
    [
      beginExport,
      endExport,
      groupGuid,
      openBase64Preview,
      periodNumber,
      queryClient,
      resolveTargetIncident,
      searchParams,
      user,
    ]
  );

  const value = useMemo(
    () => ({
      exportAsForm,
      exportPlannedResourceAsForm,
      exportWorkAssignmentListAttachment,
      exportPaginatedIAP,
      previewExportedForm,
      previewFromSignedUrl,
      isExporting: exportInFlight > 0,
    }),
    [
      exportAsForm,
      exportInFlight,
      exportPaginatedIAP,
      exportPlannedResourceAsForm,
      exportWorkAssignmentListAttachment,
      previewExportedForm,
      previewFromSignedUrl,
    ]
  );

  const base64AllowsHeaderDownload =
    downloadMode === 'base64' &&
    downloadFromBase64Ref.current?.allowWorkspaceDownload !== false;
  const showHeaderDownload =
    downloadMode === 'signed' || base64AllowsHeaderDownload;

  return (
    <IcsFormPdfPreviewContext.Provider value={value}>
      {children}
      <StylishViewFileDialog
        show={show}
        onClose={closeModal}
        fileUrl={fileUrl}
        fileName={fileName}
        onDownload={
          showHeaderDownload
            ? () => {
                void handleHeaderDownload();
              }
            : undefined
        }
        downloadLabel="Download"
        mimetype={previewMimeType}
      />
    </IcsFormPdfPreviewContext.Provider>
  );
}

export function useIcsFormPdfPreview(): IcsFormPdfPreviewContextValue {
  const ctx = useContext(IcsFormPdfPreviewContext);
  if (!ctx) {
    throw new Error(
      'useIcsFormPdfPreview must be used within IcsFormPdfPreviewProvider'
    );
  }
  return ctx;
}

/**
 * Returns the IcsFormPdfPreview context value, or `null` when rendered outside
 * `IcsFormPdfPreviewProvider` (e.g. MapPage on `/` or `/hub`).
 *
 * Intentionally does NOT use the throw-pattern because some routes render
 * resource components without the ICS provider. Callers must null-check before
 * using the returned value.
 */
export function useOptionalIcsFormPdfPreview(): IcsFormPdfPreviewContextValue | null {
  return useContext(IcsFormPdfPreviewContext);
}
